import React, { useState, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Link as MLink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Tooltip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const LAST_UPDATED = "Oct 18, 2026";
const PAGE_MAX_WIDTH = 1160;
const BODY_TEXT = "#0f172a";
const MUTED_TEXT = "#64748b";

const TOC_ITEMS = [
  { href: "#standards", text: "Standards" },
  { href: "#age", text: "Age" },
  { href: "#report", text: "How to report" },
  { href: "#handling", text: "How we handle reports" },
  { href: "#contact", text: "Contact" },
];

const SECTION_STYLE = { scrollMarginTop: 96 };

const SAFETY_MARKDOWN = `# CivixThread — Child Safety Standards

Last updated: ${LAST_UPDATED}

CivixThread prohibits child sexual abuse material (CSAM) and child sexual abuse and exploitation (CSAE). Report concerns in the app or by emailing support@civixthread.com.

## Standards
CivixThread has zero tolerance for child sexual abuse material (CSAM) and child sexual abuse and exploitation (CSAE). You may not create, upload, share, solicit, or link to this material.

This prohibition applies to posts, comments, images, profiles, and any other user content on the Service.

This page supplements our Terms of Service and Privacy Policy.

## Age
You must be at least 18 years old to use CivixThread. We do not knowingly collect personal data from anyone under 18.

## How to report
You can report a post, comment, or profile in the web and mobile apps. Choose a reason such as harassment, violence, or Other, and include details if the concern involves child safety.

You can also email support@civixthread.com.

## How we handle reports
Reports are placed in a review queue. We review reports and may remove content, restrict features, or suspend or terminate accounts, as described in our Terms of Service.

We cooperate with law enforcement when legally required.

## Contact
Questions about these standards? Email us at support@civixthread.com.
`;

export default function ChildSafetyStandardsPage() {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(SAFETY_MARKDOWN).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", py: { xs: 4, md: 6 } }}>
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: "auto" }}>
          <Box sx={{ mb: 5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: BODY_TEXT,
              letterSpacing: "-0.02em",
              mb: 0.5,
            }}
          >
            Child Safety Standards
          </Typography>
          <Typography variant="body2" sx={{ color: MUTED_TEXT }}>
            Last updated: {LAST_UPDATED}
          </Typography>
          </Box>
          <Tooltip title={copied ? "Copied!" : "Copy as Markdown"} arrow>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
              onClick={handleCopy}
              sx={{
                textTransform: "none",
                fontSize: 13,
                fontWeight: 500,
                color: copied ? "#047857" : BODY_TEXT,
                borderColor: copied ? "#047857" : "#94a3b8",
                bgcolor: copied ? "#f0fdf4" : "#fff",
                whiteSpace: "nowrap",
                flexShrink: 0,
                minHeight: 36,
                px: 1.5,
                py: 0.75,
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#047857",
                  color: "#047857",
                  bgcolor: "#f0fdf4",
                  boxShadow: "none",
                },
              }}
            >
              {copied ? "Copied!" : "Copy as Markdown"}
            </Button>
          </Tooltip>
          </Box>

          <Box sx={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
          <Box
            component="nav"
            sx={{
              display: { xs: "none", lg: "block" },
              width: 220,
              flexShrink: 0,
              position: "sticky",
              top: 96,
              borderRight: "1px solid",
              borderColor: "divider",
              pr: 4,
              py: 1,
            }}
          >
            <Typography
              variant="overline"
              sx={{
                fontWeight: 600,
                color: MUTED_TEXT,
                letterSpacing: 1,
                fontSize: 11,
                display: "block",
                mb: 1.5,
              }}
            >
              On this page
            </Typography>
            <List dense disablePadding sx={{ "& .MuiListItem-root": { px: 0 } }}>
              {TOC_ITEMS.map((item) => (
                <ListItem key={item.href} disableGutters sx={{ py: 0.4 }}>
                  <ListItemText
                    primary={
                      <MLink
                        href={item.href}
                        underline="hover"
                        sx={{
                          fontSize: 14,
                          color: BODY_TEXT,
                          fontWeight: 500,
                          "&:hover": { color: "#047857" },
                        }}
                      >
                        {item.text}
                      </MLink>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          <Box sx={{ flex: 1, maxWidth: 720, minWidth: 0 }}>
            <Box
              sx={{
                p: 3,
                mb: 5,
                bgcolor: "#fff",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "#e2e8f0",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1 }}>
                Summary
              </Typography>
              <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, fontSize: 15 }}>
                CivixThread prohibits child sexual abuse material (CSAM) and child sexual abuse and
                exploitation (CSAE). Report concerns in the app or by emailing{" "}
                <MLink href="mailto:support@civixthread.com" sx={{ color: "#047857", fontWeight: 500 }}>
                  support@civixthread.com
                </MLink>
                .
              </Typography>
            </Box>

            <Box sx={{ display: { lg: "none" }, mb: 4 }}>
              <Typography variant="overline" sx={{ fontWeight: 600, color: MUTED_TEXT, letterSpacing: 1, fontSize: 11 }}>
                On this page
              </Typography>
              <List dense sx={{ mt: 0.5 }}>
                {TOC_ITEMS.map((item) => (
                  <ListItem key={item.href} disableGutters sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckCircleOutlineIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <MLink href={item.href} underline="hover" sx={{ color: "#047857", fontSize: 14 }}>
                          {item.text}
                        </MLink>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box component="article" sx={{ "& section": { mb: 4 } }}>
              <section id="standards" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  Standards
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 1.5 }}>
                  CivixThread has zero tolerance for child sexual abuse material (CSAM) and child
                  sexual abuse and exploitation (CSAE). You may not create, upload, share, solicit,
                  or link to this material.
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 1.5 }}>
                  This prohibition applies to posts, comments, images, profiles, and any other user
                  content on the Service.
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  This page supplements our{" "}
                  <MLink href="/terms" sx={{ color: "#047857", fontWeight: 500 }}>
                    Terms of Service
                  </MLink>{" "}
                  and{" "}
                  <MLink href="/privacy-policy" sx={{ color: "#047857", fontWeight: 500 }}>
                    Privacy Policy
                  </MLink>
                  .
                </Typography>
              </section>

              <section id="age" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  Age
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  You must be at least 18 years old to use CivixThread. We do not knowingly collect
                  personal data from anyone under 18.
                </Typography>
              </section>

              <section id="report" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  How to report
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 1.5 }}>
                  You can report a post, comment, or profile in the web and mobile apps. Choose a
                  reason such as harassment, violence, or Other, and include details if the concern
                  involves child safety.
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  You can also email{" "}
                  <MLink href="mailto:support@civixthread.com" sx={{ color: "#047857", fontWeight: 500 }}>
                    support@civixthread.com
                  </MLink>
                  .
                </Typography>
              </section>

              <section id="handling" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  How we handle reports
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 1.5 }}>
                  Reports are placed in a review queue. We review reports and may remove content,
                  restrict features, or suspend or terminate accounts, as described in our{" "}
                  <MLink href="/terms" sx={{ color: "#047857", fontWeight: 500 }}>
                    Terms of Service
                  </MLink>
                  .
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  We cooperate with law enforcement when legally required.
                </Typography>
              </section>

              <section id="contact" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  Contact
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 2 }}>
                  Questions about these standards? Email us at{" "}
                  <MLink href="mailto:support@civixthread.com" sx={{ color: "#047857", fontWeight: 500 }}>
                    support@civixthread.com
                  </MLink>
                  .
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />}
                  href="/"
                  sx={{
                    bgcolor: "#047857",
                    color: "#ffffff",
                    "&:hover": { bgcolor: "#065f46", color: "#ffffff" },
                  }}
                >
                  Back to home
                </Button>
              </section>
            </Box>
          </Box>
        </Box>
        </Box>
      </Container>
    </Box>
  );
}
